import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Layout } from '@/components/Layout';
import { RSVPList } from '@/components/RSVPList';
import { QRCodeDisplay } from '@/components/QRCodeDisplay';
import { useEvents } from '@/hooks/useEvents';

export function EventAdmin() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { currentEvent, rsvps, getEvent, listRSVPs, deleteEvent, updateEvent, loading, error } = useEvents();
  const [activeTab, setActiveTab] = useState<'rsvps' | 'qr' | 'settings'>('rsvps');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    if (id) {
      getEvent(id);
      listRSVPs(id);
    }
  }, [id, getEvent, listRSVPs]);

  if (!currentEvent) {
    if (!loading && error) {
      return (
        <Layout>
          <div className="text-center py-20">
            <p className="text-gray-500">Event not found</p>
          </div>
        </Layout>
      );
    }
    return (
      <Layout>
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-violet-600" />
        </div>
      </Layout>
    );
  }

  const eventUrl = `https://aboutbloodytime.love/${currentEvent.slug}`;
  const eventDate = new Date(currentEvent.date).toLocaleDateString('en-AU', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 font-display">{currentEvent.name}</h1>
            <p className="text-gray-500 mt-1">{eventDate} &bull; {currentEvent.location}</p>
          </div>
          <div className="flex items-center gap-3">
            <Link to={`/events/${id}/edit`} className="btn-secondary text-sm">Edit Event</Link>
            <a href={eventUrl} target="_blank" rel="noopener noreferrer" className="btn-primary text-sm">View Page</a>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 mb-8">
          <nav className="flex gap-8">
            {(['rsvps', 'qr', 'settings'] as const).map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`pb-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab
                    ? 'border-violet-600 text-violet-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab === 'rsvps' ? 'RSVPs' : tab === 'qr' ? 'QR Code' : 'Settings'}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        {activeTab === 'rsvps' && <RSVPList rsvps={rsvps} customFields={currentEvent.custom_fields} eventName={currentEvent.name} loading={loading} />}

        {activeTab === 'qr' && (
          <div className="flex flex-col items-center py-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">Event QR Code</h2>
            <QRCodeDisplay url={eventUrl} size={300} />
          </div>
        )}

        {activeTab === 'settings' && (
          <div className="space-y-6 max-w-lg">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Event URL</label>
              <div className="flex items-center gap-2">
                <input type="text" readOnly value={eventUrl} className="input-field text-sm bg-gray-50" />
                <button onClick={() => navigator.clipboard.writeText(eventUrl)} className="btn-secondary text-sm whitespace-nowrap">Copy</button>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Theme</label>
              <p className="text-sm text-gray-500 capitalize">{currentEvent.theme.preset}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Custom Fields</label>
              <p className="text-sm text-gray-500">{currentEvent.custom_fields.length} custom field(s) configured</p>
            </div>
            <div>
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={currentEvent.allow_maybe !== false}
                  onChange={async () => {
                    if (!id) return;
                    await updateEvent(id, { allow_maybe: currentEvent.allow_maybe === false });
                    getEvent(id);
                  }}
                  className="w-4 h-4 rounded border-gray-300 text-violet-600 focus:ring-violet-500"
                />
                <span className="text-sm font-medium text-gray-700">Allow 'Maybe' responses</span>
              </label>
              <p className="text-xs text-gray-500 mt-1 ml-7">When disabled, guests can only respond with Yes or No.</p>
            </div>
            <Link to={`/events/${id}/edit`} className="btn-primary inline-block text-sm">Edit All Settings</Link>

            {/* Change Password */}
            <div className="mt-8 pt-6 border-t border-gray-200">
              <h3 className="text-sm font-medium text-gray-700 mb-3">Change Event Password</h3>
              <p className="text-sm text-gray-500 mb-3">
                Set a new password that guests will use to access the RSVP page.
              </p>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  placeholder="Enter new password"
                  value={newPassword}
                  onChange={(e) => { setNewPassword(e.target.value); setPasswordMessage(null); }}
                  className="input-field text-sm flex-1"
                />
                <button
                  onClick={async () => {
                    if (!newPassword.trim() || !id) return;
                    setPasswordSaving(true);
                    setPasswordMessage(null);
                    const result = await updateEvent(id, { password: newPassword.trim() });
                    setPasswordSaving(false);
                    if (result) {
                      setPasswordMessage({ type: 'success', text: `Password changed to "${newPassword.trim()}"` });
                      setNewPassword('');
                    } else {
                      setPasswordMessage({ type: 'error', text: 'Failed to update password' });
                    }
                  }}
                  disabled={!newPassword.trim() || passwordSaving}
                  className="btn-primary text-sm disabled:opacity-50 whitespace-nowrap"
                >
                  {passwordSaving ? 'Saving...' : 'Update Password'}
                </button>
              </div>
              {passwordMessage && (
                <p className={`text-sm mt-2 ${passwordMessage.type === 'success' ? 'text-green-600' : 'text-red-600'}`}>
                  {passwordMessage.text}
                </p>
              )}
            </div>

            {/* Danger Zone */}
            <div className="mt-10 pt-8 border-t border-red-100">
              <h3 className="text-sm font-semibold text-red-700 uppercase tracking-wide mb-2">Danger Zone</h3>
              <p className="text-sm text-gray-600 mb-4">
                Deleting this event will remove it from your dashboard and make the public page inaccessible. This action cannot be undone.
              </p>
              <button
                onClick={() => setShowDeleteConfirm(true)}
                className="px-4 py-2 text-sm font-medium text-red-700 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 hover:border-red-300 transition-colors"
              >
                Delete Event
              </button>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowDeleteConfirm(false)} />
            <div className="relative bg-white rounded-2xl shadow-xl max-w-md w-full p-6 animate-fade-in">
              <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-red-100 flex items-center justify-center">
                <svg className="w-6 h-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 text-center mb-2">Delete "{currentEvent.name}"?</h3>
              <p className="text-sm text-gray-600 text-center mb-6">
                This will permanently remove this event from your dashboard. Guests will no longer be able to access the RSVP page at <span className="font-medium">aboutbloodytime.love/{currentEvent.slug}</span>.
              </p>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  disabled={deleting}
                  className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    if (!id) return;
                    setDeleting(true);
                    const success = await deleteEvent(id);
                    setDeleting(false);
                    if (success) {
                      navigate('/dashboard');
                    }
                  }}
                  disabled={deleting}
                  className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                >
                  {deleting ? 'Deleting...' : 'Yes, Delete Event'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
